/**
 * Remember: always add new reducers to the /reducers/index.js file!
 * Also, add your initialState to './initialstate'!
 */
import * as types from '../actions/actiontypes'; //just externalizes action name constants
import initialState from './initialstate';
import {HttpStatusCodes} from '../util/uiconstants';
import {currentResource} from '../util/utils';
import {GET_OVERSIGHT_PROJECTS_BY_ENGAGEMENT, GET_OVERSIGHT_OTHER_PROJECTS_BY_ENGAGEMENT} from '../actions/actiontypes';
import Env from '../util/env';
import _ from 'lodash';

/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      store.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */
export default function errorReducer(state = initialState.errors, action) {
	switch (action.type) {
		case types.API_ERRORS: {
			//Hiding error toast message when user doesn't have oversight user created
			if (
				action?.action?.type === GET_OVERSIGHT_PROJECTS_BY_ENGAGEMENT ||
				action?.action?.type === GET_OVERSIGHT_OTHER_PROJECTS_BY_ENGAGEMENT
			) {
				if (action?.action?.error?.response?.status === 403 || action?.action?.error?.response?.status === 106) {
					return state;
				}
			}

			//Hiding error message notification when universal call fails, for nav.js purposes, and Archive
			if (
				action.action?.payload?.supressErrorOnUi ||
				action?.supressErrorOnUi ||
				action?.error?.response?.status === HttpStatusCodes.LengthRequired
			) {
				// don't show error on UI for e.g: we don't want to show error on UI if oversight and Iris api calls failed
				return state;
			}

			//If application gateway is rejecting input messages due to some suspicious special characters(forbidden by EY Information Security),
			if (action.error?.response?.status === 403 && action?.error?.response?.data?.errors === undefined) {
				return {
					message: currentResource.Errors[200001],
					level: 'error'
				};
			}

			/* Notice that this is simply returning the action payload, <%=names.camel%>.
			 * This is because the combineReducers() call wraps this reducer
			 * and passes it only the portion of the store state that it
			 * is concerned with. */
			console.warn('errorReducer -> ', action.error.requestUrl);
			// Hiding error message notification when universal call fails, for nav.js purposes, and Archive
			if (action.error && action.error.requestUrl) {
				// Handle share activity api errors
				if (action?.error.requestUrl.indexOf('/LinkedForms') > 0 && action.error?.config?.method === 'post') {
					if (action?.error?.response?.data?.errors?.Error) {
						return action.error.response.data.errors.Error[0];
					}
					return action?.error?.response?.data?.errors;
				}

				if (
					action.error.requestUrl.indexOf('HelixProjects') > 0 &&
					action.error.requestUrl.indexOf('Engagements') > 0 &&
					action.error.requestUrl.indexOf('Imports') > 0 &&
					action.error?.config?.method === 'delete'
				) {
					return {
						message: currentResource.Errors[34040],
						level: 'error'
					};
				}

				// Handle doc service API error messages
				if (
					action.error.requestUrl?.startsWith(Env.getDocServiceUrl()) ||
					action.error.response?.request?.responseURL?.startsWith(Env.getDocServiceUrl())
				) {
					return handleDocServiceErrors(action) || state;
				}
				if (action.error.requestUrl.indexOf('engagementmetadata.json') > 0) {
					return state;
				}

				if (action.error.requestUrl.indexOf('workspaces.json') > 0) {
					return {
						message: currentResource.Errors[7014],
						level: 'error'
					};
				}

				if (
					action.error.requestUrl.indexOf('Forms') > 0 &&
					action.error.requestUrl.indexOf('Engagements') > 0 &&
					action.error?.config?.method === 'post'
				) {
					if (action.error.response.data.errors && action.error.response.data.errors.Error[0]) {
						const code = Number(action.error.response.data.errors.Error[0].split(' - ')[0]);

						return {
							message: currentResource.Errors[code],
							level: 'error'
						};
					}

					// if this 412 - content update is in progress
					// need to ask user to wait
					if (action.error.response?.status === 412) {
						return {
							message: currentResource.Errors[4025],
							level: 'error'
						};
					}

					return {
						message: currentResource.Errors[4024],
						level: 'error'
					};
				}

				if (action.error.requestUrl.indexOf('/Archives') > 0) {
					if (action.error.response.data.Errors) {
						action.error.response.data.errors = action.error.response.data.Errors;
						action.error.response.data.Errors = null;
					}
				}
			}

			if (action?.error?.response) {
				if (action.error.response?.data?.Errors) {
					return action.error.response.data.Errors;
				} else if (action.error.response?.data?.errors) {
					if (action.error.response.data.errors.Error) {
						return action.error.response.data.errors.Error[0];
					} else if (Object.keys(action.error.response.data.errors).length > 0) {
						let list = [];
						Object.entries(action.error.response.data.errors).forEach((error) => {
							let code = error[1][0].split(' - ')[0];
							let message = error[1][0].split(' - ')[1];

							if (!list.some((item) => item.ErrorCode === code)) {
								list.push({
									ErrorCode: code,
									Message: message
								});
							}
						});

						return _.isEqual(list, state) ? state : list;
					}
				}

				if (action.error.response?.data?.Message) {
					return action.error.response.data.Message;
				} else {
					return action.error?.response?.statusText;
				}
			} else {
				return action?.error;
			}
		}
		case types.CLIENT_VALIDATION_ERROR:
			return {
				message: action.message,
				level: 'error'
			};
		case types.SUCCESS_MESSAGE:
			return {
				message: action.message,
				level: 'success',
				autoDismissInSeconds: action.autoDismissInSeconds
			};
		case types.WARNING_MESSAGE:
			return {
				message: action.message,
				level: 'warning'
			};
		case types.INFO_MESSAGE:
			return {
				message: action.message,
				level: 'info'
			};
		case types.CLEAR_ERRORS:
			return {};
		case 'DOCHELPER_ERROR':
		case 'KRONOS_UPCHUNK_UPLOAD_ERROR': {
			switch (action.error[0]) {
				case HttpStatusCodes.PreConditionFailed:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.PreConditionFailed]};
				case HttpStatusCodes.RequestEntityTooLarge:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.RequestEntityTooLarge]};
				case HttpStatusCodes.Unauthorized:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.Unauthorized]};
				case HttpStatusCodes.LengthRequired:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.LengthRequired]};
				default:
					return {
						message: `[DocHelper Error][${action.error[1]}]: ${currentResource.Errors[action.error[0]] ||
							action.error[2]}`
					};
			}
		}
		case 'INVALID_RELATED_ENTITY': {
			const errorObject = action.error;
			const entityError = '<strong>' + errorObject[0] + ':' + '</strong>';
			return {
				message: `${entityError} ${currentResource.Errors[errorObject[0]]}`
			};
		}
		case types.IS_VALID_USER_WITH_PROJECT_AVAILABLE: {
			if (action.error?.response) {
				if (action.error.response?.data === '') {
					return {
						message: currentResource.HelixProjectValidationErrors[929],
						level: 'error'
					};
				}

				if (action.error.response.data?.error?.code !== 800 && action.error.response.data?.error?.code !== 801) {
					return {
						message: currentResource.HelixProjectValidationErrors[action.error.response.data.error.code],
						level: 'error'
					};
				}
			}
			return state;
		}

		default:
			return state;
	}
}

function handleDocServiceErrors(action) {
	const defaultErrorCode = 600;
	if (action.error.response && action.error.response.data) {
		const docServiceErrorMessage = action.error.response.data.ErrorMessage?.toLowerCase();
		const docServiceErrorCode = action.error.response.data.ErrorCode;

		if (docServiceErrorCode) {
			return createErrorItem(docServiceErrorCode);
		}
		if (docServiceErrorMessage) {
			if (docServiceErrorMessage.indexOf('the specified blob does not exist') !== -1) {
				// The error message will be picked up from the resourse file. No need to pass it
				return createErrorItem(601);
			}
			if (docServiceErrorMessage.indexOf('Document is already in collaboration.') !== -1) {
				return createErrorItem(602);
			}
			return createErrorItem(defaultErrorCode);
		}
	}
}

function createErrorItem(errorCode, errorLevel) {
	return {
		message: currentResource.Errors[errorCode],
		level: errorLevel || 'error'
	};
}
